import jsPDF from 'jspdf';
import { createBoardPackLayout, finishBoardPackPages } from '@/lib/boardPackLayout';
import { buildAssumptionRows } from '@/lib/assumptions';
import { CURRENCIES } from '@/lib/calculationEngine';

/**
 * ISO 27001 Planning Decision Pack — an executive deliverable with flowing pages.
 *
 * Data integrity: every financial figure is read straight from the
 * backend-authoritative scenario payload (a frozen share snapshot or a
 * server-recalculated comparison). This renderer never computes financial
 * values; formatting and layout are its only responsibilities.
 */
const CARBON = [23, 26, 29];
const GRAY = [102, 112, 122];
const MUTED = [138, 146, 155];
const LIGHT = [245, 246, 247];
const LINE = [221, 225, 229];

const PROVENANCE_KEYS = {
  USER_PROVIDED: 'trust.provenanceUserProvided',
  DEFAULT_ASSUMPTION: 'trust.provenanceDefaultAssumption',
  MODELLED: 'trust.provenanceModelled',
  ISO_DERIVED: 'trust.provenanceIsoDerived',
  ESTIMATED: 'trust.provenanceEstimated',
  NOT_VERIFIED: 'trust.provenanceNotVerified',
};

// Decision-relevant assumptions only — this is a board document, not an appendix.
const KEY_ASSUMPTION_IDS = [
  'consultantRate', 'internalRate', 'deliveryModel', 'effectivePersonnel',
  'locations', 'toolsCost', 'trainingCost', 'certBodyDayRate',
];

const symbolOf = (config) => CURRENCIES.find(c => c.value === config?.currency)?.symbol || '€';
const money = (value, symbol) => `${symbol}${Number(value || 0).toLocaleString()}`;

function externalSpend(scenario) {
  const year1 = scenario.tco?.years?.[0]?.components || [];
  const external = year1.find(c => c.id === 'externalConsulting');
  return external ? external.amount : null;
}

export function generateBoardPackPDF({ payload, t }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const layout = createBoardPackLayout(doc);
  const scenarios = payload.scenarios || [];
  if (scenarios.length === 0) return;

  const baseline = scenarios[0];
  const symbol = symbolOf(baseline.config);
  const decision = payload.decision || {};
  const selectedIndex = decision.preferred_scenario_index;
  const generated = payload.generated_at ? new Date(payload.generated_at) : new Date();

  // ── Page 1 ────────────────────────────────────────────────────────────────
  layout.text(decision.title || t('boardPack.title'), { size: 19, bold: true });
  if (decision.organisation) {
    layout.text(`${t('boardPack.organisation')}: ${decision.organisation}`, { color: GRAY, gap: 1 });
  }
  layout.text(`${t('boardPack.date')}: ${generated.toLocaleDateString()}`, { color: GRAY, gap: 8 });

  layout.heading(t('boardPack.executiveSummary'));
  layout.table({
    body: [
      [t('boardPack.baselineInvestment'), money(baseline.results.totalCost, symbol)],
      [t('boardPack.planningRange'),
       `${money(baseline.results.costRange.low, symbol)} – ${money(baseline.results.costRange.high, symbol)}`],
      [t('boardPack.threeYear'), baseline.tco ? money(baseline.tco.total, symbol) : '-'],
      [t('boardPack.timeline'), `${baseline.results.totalDurationWeeks} ${t('boardPack.weeks')}`],
      [t('boardPack.confidence'), t(`trust.${baseline.results.confidence?.level || 'moderate'}`)],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3, textColor: CARBON },
    columnStyles: { 0: { textColor: GRAY }, 1: { halign: 'right', fontStyle: 'bold' } },
  });

  // Leave room for wrapped scenario names, selection tags and the first row.
  layout.heading(t('boardPack.comparison'), 62);
  const columnLabel = (scenario, index) => {
    const tags = [];
    if (index === 0) tags.push(t('boardPack.baseline'));
    if (selectedIndex === index) tags.push(t('boardPack.selected'));
    return tags.length ? `${scenario.name}\n${tags.join(' · ')}` : scenario.name;
  };
  const scenarioColumnWidth = (doc.internal.pageSize.getWidth() - 40 - 42) / scenarios.length;
  layout.table({
    head: [[t('boardPack.metric'), ...scenarios.map(columnLabel)]],
    body: [
      [t('boardPack.year1'), ...scenarios.map(s => money(s.results.totalCost, symbol))],
      [t('boardPack.threeYear'), ...scenarios.map(s => s.tco ? money(s.tco.total, symbol) : '-')],
      [t('boardPack.externalSpend'), ...scenarios.map(s => {
        const spend = externalSpend(s);
        return spend === null ? '-' : money(spend, symbol);
      })],
      [t('boardPack.internalEffort'), ...scenarios.map(s => `${s.results.internalDays} ${t('boardPack.days')}`)],
      [t('boardPack.timeline'), ...scenarios.map(s => `${s.results.totalDurationWeeks} ${t('boardPack.weeks')}`)],
      [t('boardPack.confidence'), ...scenarios.map(s => t(`trust.${s.results.confidence?.level || 'moderate'}`))],
    ],
    theme: 'plain',
    styles: { fontSize: 9.5, cellPadding: 3, textColor: CARBON, lineColor: LINE, lineWidth: 0.1 },
    headStyles: { fillColor: LIGHT, textColor: CARBON, fontStyle: 'bold', fontSize: 8.5, valign: 'middle' },
    columnStyles: {
      0: { textColor: GRAY, cellWidth: 42 },
      ...Object.fromEntries(scenarios.map((_, index) => [index + 1, { cellWidth: scenarioColumnWidth }])),
    },
    didParseCell: (data) => {
      if (data.column.index > 0) data.cell.styles.halign = 'right';
      // The owner's selected scenario is highlighted subtly — never labelled
      // "recommended", and nothing is highlighted when no selection was made.
      if (selectedIndex !== null && selectedIndex !== undefined
          && data.column.index === selectedIndex + 1) {
        data.cell.styles.fillColor = [241, 248, 232];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // Supporting detail starts on a fresh page, then flows as much as needed.
  layout.newPage();
  layout.heading(t('boardPack.drivers'), 32);
  const drivers = payload.sensitivity_top || [];
  if (drivers.length > 0) {
    layout.text(t('boardPack.driversIntro'), { size: 8, color: MUTED, gap: 2 });
    layout.table({
      head: [['', t('sharedScenarios.variation'),
              t('sharedScenarios.downward'), t('sharedScenarios.upward')]],
      body: drivers.map((driver, index) => [
        `${index + 1}. ${t(`sensitivity.vars.${driver.id}`)}`,
        driver.swing || '-',
        driver.lowCostDelta ? `-${money(Math.abs(driver.lowCostDelta), symbol)}` : '-',
        driver.highCostDelta ? `+${money(driver.highCostDelta, symbol)}` : '-',
      ]),
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2.5, textColor: CARBON, lineColor: LINE, lineWidth: 0.1 },
      headStyles: { fillColor: LIGHT, textColor: CARBON, fontStyle: 'bold', fontSize: 8 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    });
  }

  layout.heading(t('boardPack.assumptions'));
  const rows = buildAssumptionRows(baseline.results.assumptions || [], {
    t, formatCost: (v) => money(v, symbol),
  }).filter(row => KEY_ASSUMPTION_IDS.includes(row.id));
  const surveillance = (baseline.tco?.assumptions || []).find(a => a.id === 'surveillanceAuditDayPct');
  if (surveillance) {
    rows.push({
      id: 'surveillanceAuditDayPct',
      label: t('tco.assumptions.surveillanceAuditDayPct'),
      value: `${surveillance.value}% · ${t('tco.units.pctAuditDays')}`,
      provenance: surveillance.provenance,
    });
  }
  layout.table({
    body: rows.map(row => [row.label, row.value, t(PROVENANCE_KEYS[row.provenance] || PROVENANCE_KEYS.MODELLED)]),
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2.5, textColor: CARBON },
    columnStyles: {
      0: { textColor: GRAY },
      1: { halign: 'right', fontStyle: 'bold' },
      2: { halign: 'right', textColor: MUTED, fontSize: 7.5, cellWidth: 38 },
    },
  });

  if (decision.context || decision.note) {
    layout.heading(t('boardPack.decisionContext'));
    [decision.context, decision.note].filter(Boolean).forEach(text => {
      layout.text(text);
    });
  }

  const methodology = [
    t('boardPack.methodologyBody'),
    t('tco.recertExcluded'),
    t('tco.surveillanceBodyNote'),
  ].map(value => ({ value, size: 8.5, color: GRAY, gap: 3 }));
  const versions = [
    `${t('boardPack.engineVersion')}: ${payload.engine_version || '—'}`,
    `${t('boardPack.tcoVersion')}: ${payload.tco_model_version || '—'}`,
    `${t('boardPack.generated')}: ${generated.toLocaleString()}`,
  ].map(value => ({ value, size: 7.5, color: MUTED, gap: 1 }));
  const methodologyBlocks = [...methodology, ...versions];
  // Keep the caveats and their version/date provenance together, not on a
  // nearly empty continuation page containing only the version stamps.
  layout.keepTogether(methodologyBlocks, 14);
  layout.heading(t('boardPack.methodology'));
  methodologyBlocks.forEach(({ value, ...style }) => layout.text(value, style));
  finishBoardPackPages(doc, t);

  const slug = (decision.organisation || decision.title || t('boardPack.filename'))
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
  doc.save(`${t('boardPack.filename')}_${slug || 'export'}.pdf`);
}
